import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  FolderLock, Upload, Download, FileText, Lock, Trash2, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const FILE_TYPES = [
  { value: 'lead_list', label: 'Lead List' },
  { value: 'work_report', label: 'Work Report' }
];

export default function DropzonePage() {
  const { api, user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewFilename, setPreviewFilename] = useState('');
  
  const [uploadData, setUploadData] = useState({
    filename: '',
    file_type: '',
    content: ''
  });

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/dropzone/files');
      setFiles(response.data);
    } catch (error) {
      toast.error('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadData({
          ...uploadData,
          filename: file.name,
          content: btoa(event.target.result)
        });
      };
      reader.readAsText(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.filename || !uploadData.file_type || !uploadData.content) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await api.post('/dropzone/upload', uploadData);
      toast.success('File uploaded securely');
      setShowUploadModal(false);
      setUploadData({ filename: '', file_type: '', content: '' });
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/dropzone/download/${fileId}`);
      const content = atob(response.data.content);
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('File downloaded');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handlePreview = async (fileId, filename) => {
    try {
      const response = await api.get(`/dropzone/download/${fileId}`);
      setPreviewContent(atob(response.data.content));
      setPreviewFilename(filename);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error('Preview failed');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dropzone-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Secure <span className="text-[#D4AF37]">Dropzone</span>
            </h1>
            <p className="text-white/50 mt-1">Encrypted file storage with full audit trail</p>
          </div>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-[#D4AF37] text-[#0A091E] hover:bg-[#D4AF37]/80"
            data-testid="btn-upload"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload File
          </Button>
        </div>

        {/* Security Banner */}
        <Card className="glass-card border-0 border-l-4 border-l-[#D4AF37]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">AES-256 Encrypted Storage</h3>
                <p className="text-white/50 text-sm">
                  All files are encrypted at rest using AES-256 encryption. Every upload, download, and access 
                  is logged in the immutable audit trail for ISO/GDPR compliance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Files List */}
        <Card className="glass-card border-0">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white">
              <FolderLock className="w-5 h-5 text-[#D4AF37]" />
              Your Files
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-white/50">Loading files...</div>
            ) : files.length === 0 ? (
              <div className="p-8 text-center">
                <FolderLock className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No files uploaded yet</p>
                <Button
                  onClick={() => setShowUploadModal(true)}
                  className="mt-4 bg-[#D4AF37] text-[#0A091E]"
                >
                  Upload Your First File
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {files.map((file) => (
                  <div 
                    key={file.id} 
                    className="p-4 flex items-center justify-between hover:bg-white/5"
                    data-testid={`file-${file.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#1F1B71] flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{file.filename}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="bg-[#4B167A]/40 text-white text-xs">
                            {file.file_type.replace('_', ' ')}
                          </Badge>
                          <span className="text-white/50 text-xs">{formatDate(file.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {file.encrypted && (
                        <Lock className="w-4 h-4 text-[#10B981]" />
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePreview(file.id, file.filename)}
                        className="text-white/70 hover:text-white hover:bg-white/5"
                        data-testid={`btn-preview-${file.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(file.id, file.filename)}
                        className="text-[#D4AF37] hover:text-[#D4AF37]/80 hover:bg-white/5"
                        data-testid={`btn-download-${file.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#D4AF37]" />
              Upload Secure File
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">File Type</Label>
              <Select
                value={uploadData.file_type}
                onValueChange={(value) => setUploadData({ ...uploadData, file_type: value })}
              >
                <SelectTrigger className="bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-file-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  {FILE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Select File</Label>
              <Input
                type="file"
                onChange={handleFileSelect}
                accept=".txt,.csv,.json"
                className="bg-[#0A091E]/50 border-white/20 text-white file:bg-[#D4AF37] file:text-[#0A091E] file:border-0 file:mr-4 file:px-4 file:py-2 file:rounded-lg"
                data-testid="input-file"
              />
              <p className="text-white/30 text-xs">Supported: .txt, .csv, .json</p>
            </div>

            {uploadData.filename && (
              <div className="p-3 rounded-lg bg-[#0A091E]/50 border border-white/10">
                <p className="text-white/70 text-sm">Selected: {uploadData.filename}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUploadModal(false)} className="text-white/70">
              Cancel
            </Button>
            <Button onClick={handleUpload} className="bg-[#D4AF37] text-[#0A091E]" data-testid="btn-submit-upload">
              Upload Securely
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              {previewFilename}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <pre className="p-4 rounded-lg bg-[#0A091E] border border-white/10 text-white/70 text-sm overflow-auto max-h-96 font-mono">
              {previewContent}
            </pre>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPreviewModal(false)} className="bg-[#D4AF37] text-[#0A091E]">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
